import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ReplayIcon from '@mui/icons-material/Replay';
import api from '../services/api';

const CHECK_ASPECT = 2.3;

export default function CheckCapture({ open, onClose, onCaptured }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      setError(''); setReady(false); setPreview(null);
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera not available on this device.'); return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }, audio: false
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        setError('Could not access the camera. Check permissions, or use file upload instead.');
      }
    }
    if (open) start();
    return () => { cancelled = true; stopStream(); };
  }, [open, stopStream]);

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const vw = video.videoWidth, vh = video.videoHeight;
    if (!vw || !vh) return;
    const guideW = vw * 0.9;
    const guideH = Math.min(vh * 0.9, guideW / CHECK_ASPECT);
    const gx = (vw - guideW) / 2;
    const gy = (vh - guideH) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = guideW; canvas.height = guideH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, gx, gy, guideW, guideH, 0, 0, guideW, guideH);
    canvas.toBlob((blob) => {
      if (!blob) return;
      setPreview({ blob, url: URL.createObjectURL(blob) });
    }, 'image/jpeg', 0.92);
  };

  const retake = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const usePhoto = async () => {
    if (!preview) return;
    setUploading(true);
    try {
      const fd = new FormData();
      const filename = `check-${Date.now()}.jpg`;
      fd.append('file', preview.blob, filename);
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onCaptured({ url: res.data.url, name: res.data.filename || filename, size: res.data.size || preview.blob.size });
      handleClose();
    } catch (e) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    stopStream();
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null); setReady(false); setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Capture check</DialogTitle>
      <DialogContent>
        {error && <Typography variant="body2" sx={{ color: '#c62828', mb: 1 }}>{error}</Typography>}
        <Box sx={{ position: 'relative', width: '100%', bgcolor: '#000', borderRadius: 2, overflow: 'hidden', aspectRatio: '3 / 4' }}>
          {!preview && (
            <>
              <video ref={videoRef} playsInline muted
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                width: '90%', aspectRatio: String(CHECK_ASPECT),
                border: '3px solid rgba(46,125,50,0.9)', borderRadius: 1,
                boxShadow: '0 0 0 2000px rgba(0,0,0,0.35)'
              }} />
              <Typography variant="caption" sx={{
                position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center', color: '#fff'
              }}>
                Line the check up inside the frame
              </Typography>
              {!ready && <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}><CircularProgress sx={{ color: '#fff' }} /></Box>}
            </>
          )}
          {preview && (
            <img src={preview.url} alt="check preview" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        {!preview ? (
          <Button variant="contained" startIcon={<CameraAltIcon />} onClick={capture} disabled={!ready || !!error}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>Capture</Button>
        ) : (
          <>
            <Button startIcon={<ReplayIcon />} onClick={retake} disabled={uploading}>Retake</Button>
            <Button variant="contained" onClick={usePhoto} disabled={uploading}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
              {uploading ? 'Uploading…' : 'Use photo'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
